import { useState, useEffect } from "react";
import { formatCurrency } from "../utils/cmsApi";

const ALL_YEARS = [2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025];

// ── Shared CSV download helper ────────────────────────────────────────────────
function downloadCsv(rows, filename) {
  const csv = rows.map((r) => r.map((v) => `"${v ?? ""}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}

// ── Shared dual-axis bar chart engine ─────────────────────────────────────────
// leftColor / rightColor: rgba strings
// leftLabel / rightLabel: legend labels
// leftByYear / rightByYear: [{year, value}]
// selectedDual: the conflict record for the selected company
// hoveredYear / setHoveredYear: state from parent
function DualAxisBars({
  leftByYear, rightByYear,
  leftColor, rightColor,
  leftLabel, rightLabel,
  hoveredYear, setHoveredYear,
}) {
  const maxLeft  = Math.max(...leftByYear.map((y)  => y.value), 1);
  const maxRight = Math.max(...rightByYear.map((y) => y.value), 1);

  function fmt(v) {
    if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000)    return `$${(v / 1000).toFixed(0)}K`;
    return `$${v.toFixed(0)}`;
  }

  return (
    <div className="relative" style={{ height: "180px" }}>
      {/* Left Y-axis */}
      <div className="absolute left-0 top-0 bottom-6 flex flex-col justify-between items-end pr-1 pointer-events-none" style={{ width: "36px" }}>
        {[1, 0.75, 0.5, 0.25, 0].map((pct) => (
          <span key={pct} style={{ fontSize: "8px", color: leftColor.replace(/[\d.]+\)$/, "0.5)") }}>
            {fmt(maxLeft * pct)}
          </span>
        ))}
      </div>
      {/* Right Y-axis */}
      <div className="absolute right-0 top-0 bottom-6 flex flex-col justify-between items-start pl-1 pointer-events-none" style={{ width: "36px" }}>
        {[1, 0.75, 0.5, 0.25, 0].map((pct) => (
          <span key={pct} style={{ fontSize: "8px", color: rightColor.replace(/[\d.]+\)$/, "0.5)") }}>
            {fmt(maxRight * pct)}
          </span>
        ))}
      </div>

      {/* Bars */}
      <div className="absolute inset-0 flex items-end gap-1.5 pb-6" style={{ left: "38px", right: "38px" }}>
        {ALL_YEARS.map((y) => {
          const left  = leftByYear.find((d)  => d.year === y);
          const right = rightByYear.find((d) => d.year === y);
          const leftPct  = left  ? (left.value  / maxLeft)  * 100 : 0;
          const rightPct = right ? (right.value / maxRight) * 100 : 0;
          const hasData  = leftPct > 0 || rightPct > 0;
          const isHov    = hoveredYear === y;

          return (
            <div
              key={y}
              className="flex-1 flex items-end justify-center gap-0.5 h-full relative"
              onMouseEnter={() => setHoveredYear(y)}
              onMouseLeave={() => setHoveredYear(null)}
              style={{ cursor: hasData ? "pointer" : "default" }}
            >
              {/* Left value label */}
              {leftPct > 0 && (
                <div className="absolute left-0 text-center pointer-events-none"
                  style={{ bottom: `calc(${Math.max(leftPct, 3)}% + 2px)`, width: "calc(50% - 1px)", fontSize: "8px" }}>
                  <span style={{ color: leftColor.replace(/[\d.]+\)$/, "0.7)") }}>{fmt(left.value)}</span>
                </div>
              )}
              {/* Right value label */}
              {rightPct > 0 && (
                <div className="absolute right-0 text-center pointer-events-none"
                  style={{ bottom: `calc(${Math.max(rightPct, 3)}% + 2px)`, width: "calc(50% - 1px)", fontSize: "8px" }}>
                  <span style={{ color: rightColor.replace(/[\d.]+\)$/, "0.7)") }}>{fmt(right.value)}</span>
                </div>
              )}
              {/* Left bar */}
              <div className="flex-1 rounded-t" style={{
                height: leftPct > 0 ? `${Math.max(leftPct, 3)}%` : "2px",
                backgroundColor: leftPct > 0 ? (isHov ? leftColor.replace(/[\d.]+\)$/, "1)") : leftColor) : "rgba(255,255,255,0.04)",
                transition: "background-color 0.15s",
              }} />
              {/* Right bar */}
              <div className="flex-1 rounded-t" style={{
                height: rightPct > 0 ? `${Math.max(rightPct, 3)}%` : "2px",
                backgroundColor: rightPct > 0 ? (isHov ? rightColor.replace(/[\d.]+\)$/, "1)") : rightColor) : "rgba(255,255,255,0.04)",
                transition: "background-color 0.15s",
              }} />
            </div>
          );
        })}
      </div>

      {/* Year labels */}
      <div className="absolute bottom-0 flex gap-1.5" style={{ left: "38px", right: "38px" }}>
        {ALL_YEARS.map((y) => (
          <div key={y} className="flex-1 text-center">
            <span className={hoveredYear === y ? "text-white/60" : "text-white/25"} style={{ fontSize: "10px" }}>{y}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Shared company selector + summary cards shell ─────────────────────────────
function DualChartShell({
  title, description,
  conflicts,               // array of conflict records — one per company
  selectedCompanyId, setSelectedCompanyId,
  hoveredYear, setHoveredYear,
  leftLabel, rightLabel,
  leftColor, rightColor,
  leftSummaryLabel, rightSummaryLabel,
  getLeftSummaryValue,     // fn(conflict) → string
  getRightSummaryValue,    // fn(conflict) → string
  getSamePeriodFlag,       // fn(conflict) → bool
  samePeriodLabel,
  children,                // the DualAxisBars + legend + export
}) {
  const selected = conflicts.find((c) => c.companyId === selectedCompanyId) || conflicts[0];

  return (
    <div className="card mb-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-white/60 text-xs uppercase tracking-widest">{title}</h2>
          <p className="text-white/25 text-xs mt-1">{description}</p>
        </div>
        {hoveredYear && (
          <div className="text-right shrink-0">
            <p className="text-white/40 text-xs font-medium">{hoveredYear}</p>
          </div>
        )}
      </div>

      {/* Company selector */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {conflicts.length === 1 ? (
          <span className="text-xs px-3 py-1.5 rounded-lg border bg-amber-500/20 border-amber-500/30 text-amber-400">
            {conflicts[0].companyName.length > 32 ? conflicts[0].companyName.slice(0, 32) + "…" : conflicts[0].companyName}
          </span>
        ) : (
          conflicts.map((c) => (
            <button
              key={c.companyId}
              onClick={() => { setSelectedCompanyId(c.companyId); setHoveredYear(null); }}
              className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${
                selectedCompanyId === c.companyId
                  ? "bg-amber-500/20 border-amber-500/30 text-amber-400"
                  : "border-white/10 text-white/40 hover:border-white/20 hover:text-white/60"
              }`}
            >
              {c.companyName.length > 32 ? c.companyName.slice(0, 32) + "…" : c.companyName}
            </button>
          ))
        )}
      </div>

      {/* Summary cards */}
      {selected && (
        <div className="flex gap-3 mb-4 flex-wrap">
          <div className="flex-1 min-w-0 rounded-lg px-3 py-2 border"
            style={{ backgroundColor: leftColor.replace(/[\d.]+\)$/, "0.05)"), borderColor: leftColor.replace(/[\d.]+\)$/, "0.15)") }}>
            <p className="text-xs" style={{ color: leftColor.replace(/[\d.]+\)$/, "0.6)") }}>{leftSummaryLabel}</p>
            <p className="text-sm font-medium mt-0.5" style={{ color: leftColor }}>{getLeftSummaryValue(selected)}</p>
          </div>
          <div className="flex-1 min-w-0 rounded-lg px-3 py-2 border"
            style={{ backgroundColor: rightColor.replace(/[\d.]+\)$/, "0.05)"), borderColor: rightColor.replace(/[\d.]+\)$/, "0.15)") }}>
            <p className="text-xs" style={{ color: rightColor.replace(/[\d.]+\)$/, "0.6)") }}>{rightSummaryLabel}</p>
            <p className="text-sm font-medium mt-0.5" style={{ color: rightColor }}>{getRightSummaryValue(selected)}</p>
          </div>
          {getSamePeriodFlag(selected) && (
            <div className="flex items-center px-3 py-2 bg-orange-500/10 border border-orange-500/20 rounded-lg shrink-0">
              <span className="text-orange-400 text-xs">⚑ {samePeriodLabel}</span>
            </div>
          )}
        </div>
      )}

      {children}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// CHART 1: General Payments vs. Ownership Value
// Used on: PhysicianPage (A), PhysicianOwnersPage (A)
// conflicts = getDualInterestByNPI results (payments+ownership)
// generalPayments = getGeneralPaymentsByNPIAndCompany results
// ownershipDetail = getOwnershipDetailByNPI results
// ══════════════════════════════════════════════════════════════════════════════
export function GeneralVsOwnershipChart({ conflicts, generalPayments, ownershipDetail, providerName, npi }) {
  const [hoveredYear, setHoveredYear] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(conflicts[0]?.companyId || null);

  useEffect(() => {
    if (conflicts.length > 0 && !selectedCompanyId) setSelectedCompanyId(conflicts[0].companyId);
  }, [conflicts]);

  if (!conflicts.length) return null;

  const LEFT_COLOR  = "rgba(96,165,250,0.75)";   // blue — general payments
  const RIGHT_COLOR = "rgba(251,191,36,0.80)";   // amber — ownership value

  const generalByYear = ALL_YEARS.map((y) => ({
    year: y,
    value: generalPayments
      .filter((p) => p.year === y && p.companyId === selectedCompanyId)
      .reduce((s, r) => s + r.totalPayments, 0),
  }));

  const ownershipByYear = ALL_YEARS.map((y) => ({
    year: y,
    value: ownershipDetail
      .filter((o) => o.programYear === y && o.companyId === selectedCompanyId)
      .reduce((s, o) => s + o.valueOfInterest, 0),
  }));

  const hasData = generalByYear.some((y) => y.value > 0) || ownershipByYear.some((y) => y.value > 0);
  if (!hasData) return null;

  const selected = conflicts.find((c) => c.companyId === selectedCompanyId) || conflicts[0];

  function exportCsv() {
    const rows = [["Provider Name", "NPI", "Company", "Year", "General Payments ($)", "Ownership Value ($)"]];
    for (const g of generalByYear) {
      const o = ownershipByYear.find((d) => d.year === g.year);
      if (g.value > 0 || (o?.value || 0) > 0) {
        rows.push([providerName, npi, selected?.companyName || "", g.year, g.value.toFixed(2), (o?.value || 0).toFixed(2)]);
      }
    }
    downloadCsv(rows, `${(providerName || "provider").replace(/\s+/g, "_")}_general_vs_ownership.csv`);
  }

  return (
    <DualChartShell
      title="Dual Interest · General payments vs. ownership value"
      description="Companies where this provider received general payments and holds or held an ownership interest"
      conflicts={conflicts}
      selectedCompanyId={selectedCompanyId}
      setSelectedCompanyId={setSelectedCompanyId}
      hoveredYear={hoveredYear}
      setHoveredYear={setHoveredYear}
      leftLabel="General payments"
      rightLabel="Ownership value"
      leftColor={LEFT_COLOR}
      rightColor={RIGHT_COLOR}
      leftSummaryLabel="Total general payments"
      rightSummaryLabel="Ownership period"
      getLeftSummaryValue={(c) => formatCurrency(c.generalPaymentsTotal || 0)}
      getRightSummaryValue={(c) => c.ownershipFirstYear === c.ownershipLatestYear
        ? String(c.ownershipFirstYear)
        : `${c.ownershipFirstYear}–${c.ownershipLatestYear}`}
      getSamePeriodFlag={(c) => c.hasDualInterestSamePeriod}
      samePeriodLabel="Same period overlap"
    >
      <DualAxisBars
        leftByYear={generalByYear}
        rightByYear={ownershipByYear}
        leftColor={LEFT_COLOR}
        rightColor={RIGHT_COLOR}
        leftLabel="General payments"
        rightLabel="Ownership value"
        hoveredYear={hoveredYear}
        setHoveredYear={setHoveredYear}
      />
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: LEFT_COLOR }} />
            <span className="text-white/40 text-xs">General payments</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: RIGHT_COLOR }} />
            <span className="text-white/40 text-xs">Ownership value</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-white/30 text-xs">Each axis scaled independently</p>
          <button onClick={exportCsv}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all">
            ⬇ Export
          </button>
        </div>
      </div>
    </DualChartShell>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// CHART 2: General Payments vs. Research Funding
// Used on: PhysicianPage (B), PhysicianResearchPage (B)
// conflicts = getResearchGeneralDualInterestByNPI results
// generalPayments = getGeneralPaymentsByNPIAndCompany results
// researchDetail = getResearchDetailByNPI results
// ══════════════════════════════════════════════════════════════════════════════
export function GeneralVsResearchChart({ conflicts, generalPayments, researchDetail, directResearch, providerName, npi }) {
  const [hoveredYear, setHoveredYear] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(conflicts[0]?.companyId || null);

  useEffect(() => {
    if (conflicts.length > 0 && !selectedCompanyId) setSelectedCompanyId(conflicts[0].companyId);
  }, [conflicts]);

  if (!conflicts.length) return null;

  const LEFT_COLOR  = "rgba(96,165,250,0.75)";   // blue — general payments
  const RIGHT_COLOR = "rgba(20,184,166,0.75)";    // teal — research funding

  const generalByYear = ALL_YEARS.map((y) => ({
    year: y,
    value: generalPayments
      .filter((p) => p.year === y && p.companyId === selectedCompanyId)
      .reduce((s, r) => s + r.totalPayments, 0),
  }));

  const researchByYear = ALL_YEARS.map((y) => ({
    year: y,
    value:
      // PI-based institutional research (research_detail_by_year)
      researchDetail
        .filter((d) => d.programYear === y && d.companyId === selectedCompanyId)
        .reduce((s, r) => s + r.totalAmount, 0) +
      // Direct research payments (physician_direct_research_agg)
      (directResearch || [])
        .filter((d) => d.year === y && d.companyId === selectedCompanyId)
        .reduce((s, d) => s + d.totalAmount, 0),
  }));

  const hasData = generalByYear.some((y) => y.value > 0) || researchByYear.some((y) => y.value > 0);
  if (!hasData) return null;

  const selected = conflicts.find((c) => c.companyId === selectedCompanyId) || conflicts[0];

  function exportCsv() {
    const rows = [["Provider Name", "NPI", "Company", "Year", "General Payments ($)", "Research Funding ($)"]];
    for (const g of generalByYear) {
      const r = researchByYear.find((d) => d.year === g.year);
      if (g.value > 0 || (r?.value || 0) > 0) {
        rows.push([providerName, npi, selected?.companyName || "", g.year, g.value.toFixed(2), (r?.value || 0).toFixed(2)]);
      }
    }
    downloadCsv(rows, `${(providerName || "provider").replace(/\s+/g, "_")}_general_vs_research.csv`);
  }

  return (
    <DualChartShell
      title="Dual Interest · General payments vs. research funding"
      description="Companies where this provider received both general payments and research funding"
      conflicts={conflicts}
      selectedCompanyId={selectedCompanyId}
      setSelectedCompanyId={setSelectedCompanyId}
      hoveredYear={hoveredYear}
      setHoveredYear={setHoveredYear}
      leftLabel="General payments"
      rightLabel="Research funding"
      leftColor={LEFT_COLOR}
      rightColor={RIGHT_COLOR}
      leftSummaryLabel="Total general payments"
      rightSummaryLabel="Total research funding"
      getLeftSummaryValue={(c) => formatCurrency(c.generalPaymentsTotal || 0)}
      getRightSummaryValue={(c) => formatCurrency(c.researchTotal || 0)}
      getSamePeriodFlag={(c) => c.hasDualInterestSamePeriod}
      samePeriodLabel="Same period overlap"
    >
      <DualAxisBars
        leftByYear={generalByYear}
        rightByYear={researchByYear}
        leftColor={LEFT_COLOR}
        rightColor={RIGHT_COLOR}
        leftLabel="General payments"
        rightLabel="Research funding"
        hoveredYear={hoveredYear}
        setHoveredYear={setHoveredYear}
      />
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: LEFT_COLOR }} />
            <span className="text-white/40 text-xs">General payments</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: RIGHT_COLOR }} />
            <span className="text-white/40 text-xs">Research funding</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-white/30 text-xs">Each axis scaled independently</p>
          <button onClick={exportCsv}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all">
            ⬇ Export
          </button>
        </div>
      </div>
    </DualChartShell>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// CHART 3: Ownership Value vs. Research Funding
// Used on: PhysicianOwnersPage (B), PhysicianResearchPage (A)
// conflicts = getResearchOwnershipDualInterest results
// ownershipDetail = getOwnershipDetailByNPI results
// researchDetail = getResearchDetailByNPI results
// ══════════════════════════════════════════════════════════════════════════════
export function OwnershipVsResearchChart({ conflicts, ownershipDetail, researchDetail, directResearch, providerName, npi }) {
  const [hoveredYear, setHoveredYear] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(conflicts[0]?.companyId || null);

  useEffect(() => {
    if (conflicts.length > 0 && !selectedCompanyId) setSelectedCompanyId(conflicts[0].companyId);
  }, [conflicts]);

  if (!conflicts.length) return null;

  const LEFT_COLOR  = "rgba(251,191,36,0.80)";   // amber — ownership value
  const RIGHT_COLOR = "rgba(20,184,166,0.75)";    // teal — research funding

  const ownershipByYear = ALL_YEARS.map((y) => ({
    year: y,
    value: ownershipDetail
      .filter((o) => o.programYear === y && o.companyId === selectedCompanyId)
      .reduce((s, o) => s + o.valueOfInterest, 0),
  }));

  const researchByYear = ALL_YEARS.map((y) => ({
    year: y,
    value:
      // PI-based institutional research (research_detail_by_year)
      researchDetail
        .filter((d) => d.programYear === y && d.companyId === selectedCompanyId)
        .reduce((s, r) => s + r.totalAmount, 0) +
      // Direct research payments (physician_direct_research_agg)
      (directResearch || [])
        .filter((d) => d.year === y && d.companyId === selectedCompanyId)
        .reduce((s, d) => s + d.totalAmount, 0),
  }));

  const hasData = ownershipByYear.some((y) => y.value > 0) || researchByYear.some((y) => y.value > 0);
  if (!hasData) return null;

  const selected = conflicts.find((c) => c.companyId === selectedCompanyId) || conflicts[0];

  function exportCsv() {
    const rows = [["Provider Name", "NPI", "Company", "Year", "Ownership Value ($)", "Research Funding ($)"]];
    for (const o of ownershipByYear) {
      const r = researchByYear.find((d) => d.year === o.year);
      if (o.value > 0 || (r?.value || 0) > 0) {
        rows.push([providerName, npi, selected?.companyName || "", o.year, o.value.toFixed(2), (r?.value || 0).toFixed(2)]);
      }
    }
    downloadCsv(rows, `${(providerName || "provider").replace(/\s+/g, "_")}_ownership_vs_research.csv`);
  }

  return (
    <DualChartShell
      title="Dual Interest · Ownership value vs. research funding"
      description="Companies where this provider holds or held an ownership interest and received research funding"
      conflicts={conflicts}
      selectedCompanyId={selectedCompanyId}
      setSelectedCompanyId={setSelectedCompanyId}
      hoveredYear={hoveredYear}
      setHoveredYear={setHoveredYear}
      leftLabel="Ownership value"
      rightLabel="Research funding"
      leftColor={LEFT_COLOR}
      rightColor={RIGHT_COLOR}
      leftSummaryLabel="Ownership period"
      rightSummaryLabel="Research period"
      getLeftSummaryValue={(c) => c.ownershipFirstYear === c.ownershipLatestYear
        ? String(c.ownershipFirstYear)
        : `${c.ownershipFirstYear}–${c.ownershipLatestYear}`}
      getRightSummaryValue={(c) => c.researchFirstYear === c.researchLatestYear
        ? String(c.researchFirstYear)
        : `${c.researchFirstYear}–${c.researchLatestYear}`}
      getSamePeriodFlag={(c) => c.hasDualInterestSamePeriod}
      samePeriodLabel="Same period overlap"
    >
      <DualAxisBars
        leftByYear={ownershipByYear}
        rightByYear={researchByYear}
        leftColor={LEFT_COLOR}
        rightColor={RIGHT_COLOR}
        leftLabel="Ownership value"
        rightLabel="Research funding"
        hoveredYear={hoveredYear}
        setHoveredYear={setHoveredYear}
      />
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: LEFT_COLOR }} />
            <span className="text-white/40 text-xs">Ownership value</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: RIGHT_COLOR }} />
            <span className="text-white/40 text-xs">Research funding</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-white/30 text-xs">Each axis scaled independently</p>
          <button onClick={exportCsv}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all">
            ⬇ Export
          </button>
        </div>
      </div>
    </DualChartShell>
  );
}